// Lib
import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Utils
import { isAlias } from '../../../common/elements/elementHelper';
import { getHybridUseCase } from '../../hybridApp/hybridAppUtils';
import { getPhysicalId } from '../../../common/elements/utils/elementPropertyUtils';
import {
    getAsyncEntityFetchedTime,
    shouldFetchAsyncResource,
} from '../../utils/services/http/asyncResource/asyncResourceUtils';

// Services
import * as boardService from './boardService';

// Selectors
import { getBoardResourceEntityState } from './boardSelector';
import { getLocalCacheHydrationTimestamp } from '../../offline/cache/localCacheSelector';

// Constants
import { HybridUseCase } from '../../hybridApp/hybridAppConstants';

const createDebouncedFetchIdsFunc = (fetchFunc) => {
    let boardFetchIds = [];
    let boardFetchAncestors = false;
    let fetchBoardTimeout;

    return (boardId, loadAncestors) => (dispatch) => {
        boardFetchIds.push(boardId);
        boardFetchAncestors = boardFetchAncestors || loadAncestors;

        clearTimeout(fetchBoardTimeout);

        fetchBoardTimeout = setTimeout(() => {
            dispatch(
                fetchFunc({
                    boardIds: boardFetchIds,
                    loadAncestors: boardFetchAncestors,
                    excludeSelf: true,
                }),
            );

            boardFetchIds = [];
            boardFetchAncestors = false;
        }, 250);
    };
};

const debouncedFetchBoard = createDebouncedFetchIdsFunc(boardService.fetchBoards);
const debouncedRefreshBoardIfModified = createDebouncedFetchIdsFunc(boardService.refreshMultipleBoardsIfModified);

const mapDispatchToProps = (dispatch) => ({
    fetchBoard: (boardId, loadAncestors) => dispatch(debouncedFetchBoard(boardId, loadAncestors)),
    refreshBoardIfModified: (boardId, loadAncestors) =>
        dispatch(debouncedRefreshBoardIfModified(boardId, loadAncestors)),
});

const mapStateToProps = () =>
    createStructuredSelector({
        boardResourceEntity: getBoardResourceEntityState,
        localCacheHydrationTimestamp: getLocalCacheHydrationTimestamp,
    });

class BoardChildrenLoadObserver extends React.Component {
    constructor(props) {
        super(props);

        this.observeDiv = createRef();

        // if browser doesn't support IntersectionObserver, just preload the board children
        if (!window.IntersectionObserver) {
            this.intersectionCallback([{ isIntersecting: true }]);
            return;
        }

        const getRootForIntersection = () => {
            switch (getHybridUseCase()) {
                case HybridUseCase.ANDROID_BOARD_LIST:
                case HybridUseCase.IOS_BOARD_LIST:
                    // As per the docs, when setting this to null, it defaults to the browser's viewport.
                    // It's not clear why it set to the App div previously, but it's maintained here in case
                    // it's intentional for certain use case.
                    return null;
                case HybridUseCase.IOS_CANVAS:
                case HybridUseCase.IPAD_OS:
                default:
                    return document.querySelector('.App');
            }
        };

        this.observer = new IntersectionObserver(this.intersectionCallback, {
            root: getRootForIntersection(),
            threshold: 0.25,
        });
    }

    componentDidMount() {
        this.observeDiv.current && this.observer && this.observer.observe(this.observeDiv.current);
    }

    componentWillUnmount() {
        this.observeDiv.current && this.observer && this.observer.unobserve(this.observeDiv.current);
    }

    intersectionCallback = async ([intersection]) => {
        const { isIntersecting } = intersection;
        const { boardResourceEntity, fetchBoard, refreshBoardIfModified, element, localCacheHydrationTimestamp } =
            this.props;

        // If the board isn't visible yet, don't worry about prefetching it
        if (!isIntersecting) return;

        // If we have the div with the intersection observer, stop observing as we don't need to action it again
        if (this.observeDiv.current) {
            this.observer && this.observer.unobserve(this.observeDiv.current);
        }

        const shouldFetchBoard = shouldFetchAsyncResource(boardResourceEntity);

        if (shouldFetchBoard) {
            return fetchBoard(getPhysicalId(element), isAlias(element));
        }

        // Otherwise we should check if the board is already fetched, but was fetched before
        //  the cache hydration time. It may have been modified since the app went offline
        //  so we should refresh if it's been modified
        if (!localCacheHydrationTimestamp) return;

        const fetchedTime = getAsyncEntityFetchedTime(boardResourceEntity);

        if (!fetchedTime || fetchedTime > localCacheHydrationTimestamp) return;

        // Perform a batched refresh if modified
        return refreshBoardIfModified(getPhysicalId(element), isAlias(element));
    };

    render() {
        const { children } = this.props;

        return <div ref={this.observeDiv}>{children}</div>;
    }
}

BoardChildrenLoadObserver.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    element: PropTypes.object,
    fetchBoard: PropTypes.func,
    refreshBoardIfModified: PropTypes.func,
    boardResourceEntity: PropTypes.object,
    localCacheHydrationTimestamp: PropTypes.number,
};

export default connect(mapStateToProps, mapDispatchToProps)(BoardChildrenLoadObserver);
